import type { Options } from '@segment/analytics-next';
import JsCookie from 'js-cookie';
import type { UtmSearchParams } from '@/router/utils';
import { HOSTNAME } from '@/helpers/url';
import { WEB_STORAGE_PREFIX } from './constants';

const Cookies = JsCookie.withAttributes({
  domain: `.${HOSTNAME}`,
  sameSite: 'Lax',
  secure: true,
});

type Campaign = NonNullable<NonNullable<Options['context']>['campaign']>;

const UTM_PARAMS_COOKIE_NAME = 'utm_params';
const INITIAL_UTM_PARAMS_WEB_STORAGE_NAME = `${WEB_STORAGE_PREFIX}initialUtmParams`;
const UTM_REGEX = /^utm_/;
const UTM_PARAMS_CACHE = new Map<string, Campaign | undefined>();

/**
 * Persists UTM params in a cookie for the time of the session.
 * Persist first touch UTM params in local storage, so they can be attributed to the user.
 */
export function storeUtmParams(search: UtmSearchParams, options?: { firstTouch?: boolean }): void {
  const value = new URLSearchParams(search).toString();
  if (options?.firstTouch) {
    if (retrieveUtmParams({ firstTouch: true })) {
      // We only do this once per user, so if the entry is already set, we don't overwrite it
      return;
    }
    window.localStorage.setItem(INITIAL_UTM_PARAMS_WEB_STORAGE_NAME, value);
  } else {
    Cookies.set(UTM_PARAMS_COOKIE_NAME, value);
    UTM_PARAMS_CACHE.delete(UTM_PARAMS_COOKIE_NAME);
  }
}

export const retrieveUtmParams = (options?: { firstTouch?: boolean }) => {
  let value;
  if (options?.firstTouch) {
    value = window.localStorage.getItem(INITIAL_UTM_PARAMS_WEB_STORAGE_NAME);
  } else {
    if (UTM_PARAMS_CACHE.has(UTM_PARAMS_COOKIE_NAME)) {
      return UTM_PARAMS_CACHE.get(UTM_PARAMS_COOKIE_NAME);
    }
    value = Cookies.get(UTM_PARAMS_COOKIE_NAME);
  }
  if (!value) {
    return undefined;
  }
  const params = new URLSearchParams(value);
  const campaign: Partial<Campaign> = {};
  for (const [k, v] of params) {
    campaign[k.replace(UTM_REGEX, '')] = v;
  }
  if (!options?.firstTouch) {
    UTM_PARAMS_CACHE.set(UTM_PARAMS_COOKIE_NAME, campaign as Campaign);
  }
  return campaign as Campaign;
};
